import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormArray, FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { filter, map, Observable, of, Subscription, switchMap } from 'rxjs';
import { UserOrderRoutes, UserStorageRoutes } from 'src/app/core/enums/routes.enum';
import { Privileges } from 'src/app/core/privileges/privileges';
import { CartOrderMergeStrategy } from 'src/app/order-user/enums/cart-order-merge-strategy.enum';
import { CartOrderTarget } from 'src/app/order-user/enums/cart-order-target.enum';
import { getCartOrderLineOverlaps } from 'src/app/order-user/functions/get-cart-order-line-overlaps';
import { IOrderLineRequest } from 'src/app/order-user/models/order-line.model';
import { IOrderResponse } from 'src/app/order-user/models/order.model';
import { OrderApiService } from 'src/app/order-user/services/order-api.service';
import {
  ApplyCartToCurrentDraft,
  CreateNewDraftFromCart,
} from 'src/app/order-user/state/cart-order.actions';
import { TenantUserPrivilegesService } from 'src/app/tenant-selector/services/tenant-user-privileges.service';
import { ICartItem } from '../../models/cart-item.model';
import { CartDraftPickerModalComponent } from '../../modals/cart-draft-picker-modal/cart-draft-picker-modal.component';
import { CartMergeStrategyModalComponent } from '../../modals/cart-merge-strategy-modal/cart-merge-strategy-modal.component';
import { ClearCart, RemoveFromCart, UpdateCartItemQuantity } from '../../state/cart.actions';
import { CartState } from '../../state/cart.state';

type CartQuantityControl = FormControl<number | null>;
type ExistingDraftCartTarget = {
  order: IOrderResponse;
  strategy?: CartOrderMergeStrategy;
};

@Component({
  selector: 'app-cart-content',
  templateUrl: './cart-content.component.html',
  styleUrls: ['./cart-content.component.scss'],
  standalone: false,
})
export class CartContentComponent implements OnInit, OnDestroy {
  @Input() variant: 'modal' | 'dropdown' = 'modal';
  @Output() readonly closeRequested = new EventEmitter<void>();

  readonly userStorageRoutes = UserStorageRoutes;
  readonly makeOrderPrivileges = [Privileges.OrderCreate, Privileges.OrderUpdate];

  items: ICartItem[] = [];
  quantities = new FormArray<CartQuantityControl>([]);

  private readonly tenantUserPrivileges = inject(TenantUserPrivilegesService);
  private subscriptions = new Subscription();
  private quantitySubscriptions = new Subscription();
  private productIds: string[] = [];

  get isDropdown(): boolean {
    return this.variant === 'dropdown';
  }

  get makeOrderDisabled(): boolean {
    return this.items.length === 0 || this.quantities.invalid;
  }

  get makeOrderTooltip(): string {
    return this.canMakeOrder ? '' : 'noAccess';
  }

  get canCreateOrder(): boolean {
    return this.tenantUserPrivileges.userHasPrivilege(Privileges.OrderCreate);
  }

  get canUpdateOrder(): boolean {
    return this.tenantUserPrivileges.userHasPrivilege(Privileges.OrderUpdate);
  }

  get canMakeOrder(): boolean {
    return this.tenantUserPrivileges.userHasPrivilege(this.makeOrderPrivileges);
  }

  get canAddToCurrentDraft(): boolean {
    const path = this.router.url.split(/[?#]/)[0];
    return path === UserOrderRoutes.OrdersNew || /^\/purchases\/drafts\/[^/]+$/.test(path);
  }

  constructor(
    private router: Router,
    private store: Store,
    private dialog: MatDialog,
    private orderApiService: OrderApiService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.store.select(CartState.getItems).subscribe((items) => {
        this.items = items;
        this.syncQuantityControls(items);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.quantitySubscriptions.unsubscribe();
  }

  close(): void {
    this.closeRequested.emit();
  }

  getQuantityControl(index: number): CartQuantityControl {
    return this.quantities.at(index);
  }

  quantityInputChanged(index: number, value: string): void {
    const control = this.getQuantityControl(index);
    const quantity = this.parseQuantity(value);

    control.setValue(quantity);
    control.markAsDirty();
    control.markAsTouched();
    control.updateValueAndValidity();
  }

  removeFromCart(productId: string): void {
    this.store.dispatch(new RemoveFromCart(productId));
  }

  clearCart(): void {
    this.store.dispatch(new ClearCart());
    this.close();
  }

  makeNewDraftOrder(): void {
    if (this.makeOrderDisabled || !this.canCreateOrder) {
      return;
    }

    if (this.isCurrentNewDraftRoute()) {
      this.close();
      this.store.dispatch(new CreateNewDraftFromCart());
      return;
    }

    this.close();
    this.router.navigate([UserOrderRoutes.OrdersNew], {
      state: { cartOrderTarget: CartOrderTarget.NewDraft },
    });
  }

  addToExistingDraft(): void {
    if (this.makeOrderDisabled || !this.canUpdateOrder) {
      return;
    }

    this.subscriptions.add(
      this.dialog
        .open(CartDraftPickerModalComponent, {
          width: '44rem',
          panelClass: ['no-padding', 'mobile-sizing-for-mat-dialogs'],
        })
        .afterClosed()
        .pipe(
          filter((order): order is IOrderResponse => !!order),
          switchMap((order) => this.loadDraftOrder(order.id)),
          switchMap((order) => this.resolveMergeStrategy(order))
        )
        .subscribe((result) => {
          if (!result) {
            return;
          }

          this.navigateToExistingDraft(result.order.id, result.strategy);
          this.close();
        })
    );
  }

  addToCurrentDraft(): void {
    if (this.makeOrderDisabled || !this.canUpdateOrder) {
      return;
    }

    this.close();
    this.store.dispatch(new ApplyCartToCurrentDraft());
  }

  private syncQuantityControls(items: ICartItem[]): void {
    if (!this.hasSameProducts(items)) {
      this.rebuildQuantityControls(items);
      return;
    }

    items.forEach((item, index) => {
      const control = this.getQuantityControl(index);
      if (control.valid && control.value !== null && control.value !== item.quantity) {
        control.setValue(item.quantity, { emitEvent: false });
      }
    });
  }

  private rebuildQuantityControls(items: ICartItem[]): void {
    this.quantitySubscriptions.unsubscribe();
    this.quantitySubscriptions = new Subscription();
    this.quantities.clear();
    this.productIds = items.map((item) => item.productId);

    items.forEach((item) => {
      const control = new FormControl<number | null>(item.quantity, {
        validators: [Validators.required, Validators.min(1)],
      });

      this.quantities.push(control);
      this.quantitySubscriptions.add(
        control.valueChanges.subscribe((quantity) => {
          if (control.invalid || quantity === null) {
            return;
          }

          this.store.dispatch(new UpdateCartItemQuantity(item.productId, quantity));
        })
      );
    });
  }

  private hasSameProducts(items: ICartItem[]): boolean {
    return (
      items.length === this.productIds.length &&
      items.every((item, index) => item.productId === this.productIds[index])
    );
  }

  private parseQuantity(value: string): number | null {
    if (value.trim() === '') {
      return null;
    }

    const quantity = Number(value);
    return Number.isFinite(quantity) ? quantity : null;
  }

  private loadDraftOrder(orderId: string): Observable<IOrderResponse> {
    return this.orderApiService
      .getOrderById(orderId)
      .pipe(filter((order): order is IOrderResponse => !!order?.id));
  }

  private resolveMergeStrategy(order: IOrderResponse): Observable<ExistingDraftCartTarget | null> {
    if (this.isCurrentDraftRoute(order.id)) {
      return of({ order });
    }

    const overlaps = getCartOrderLineOverlaps(this.items, order.orderLines as IOrderLineRequest[]);
    if (overlaps.length === 0) {
      return of({ order, strategy: CartOrderMergeStrategy.KeepDraftQuantities });
    }

    return this.dialog
      .open(CartMergeStrategyModalComponent, {
        width: '36rem',
        panelClass: ['no-padding', 'mobile-sizing-for-mat-dialogs'],
        data: { overlapCount: overlaps.length },
      })
      .afterClosed()
      .pipe(
        map((strategy: CartOrderMergeStrategy | undefined) =>
          strategy ? { order, strategy } : null
        )
      );
  }

  private navigateToExistingDraft(orderId: string, strategy?: CartOrderMergeStrategy): void {
    if (this.isCurrentDraftRoute(orderId)) {
      this.store.dispatch(new ApplyCartToCurrentDraft(strategy));
      return;
    }

    this.router.navigate([UserOrderRoutes.Orders, orderId], {
      state: {
        cartOrderTarget: CartOrderTarget.ExistingDraft,
        cartMergeStrategy: strategy,
      },
    });
  }

  private isCurrentNewDraftRoute(): boolean {
    return this.getCurrentPath() === UserOrderRoutes.OrdersNew;
  }

  private isCurrentDraftRoute(orderId: string): boolean {
    return this.getCurrentPath() === `${UserOrderRoutes.Orders}/${orderId}`;
  }

  private getCurrentPath(): string {
    return this.router.url.split(/[?#]/)[0];
  }
}
